import type {
  SessionTranscriptActionSuggestion,
  SessionTranscriptShowRefs,
  SessionTranscriptEntry,
} from "./types";
import {
  isCanonicalSessionFeedDocument,
  readSessionFeedSnapshot,
  type FeedItem,
} from "./feed";

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function trimString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function compactJson(value: unknown, maxLength = 320): string | undefined {
  if (value === undefined || value === null) return undefined;
  try {
    const json = JSON.stringify(value);
    if (!json || json === "undefined") return undefined;
    return json.length > maxLength ? `${json.slice(0, maxLength)}...` : json;
  } catch {
    return String(value);
  }
}

function artifactRecordsById(
  liveDoc?: UnknownRecord | null,
): Record<string, UnknownRecord> {
  const artifacts = asRecord(liveDoc?.artifacts);
  const byId = asRecord(artifacts?.byId) || {};
  return Object.fromEntries(
    Object.entries(byId)
      .map(([artifactId, value]) => {
        const record = asRecord(value);
        return record ? [artifactId, record] : null;
      })
      .filter((entry): entry is [string, UnknownRecord] => Boolean(entry)),
  );
}

function stringifyTranscriptValue(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value.trim() || fallback;
  if (typeof value === "boolean") return value ? "Confirmed" : "Canceled";
  if (value === undefined) return fallback;
  try {
    const json = JSON.stringify(value, null, 2);
    if (!json || json === "undefined") return fallback;
    return json.length > 2_000 ? `${json.slice(0, 2_000)}...` : json;
  } catch {
    return String(value);
  }
}

function latestInputEditSummary(metadata: UnknownRecord): string | null {
  const lastInputEdit = asRecord(metadata.lastInputEdit);
  if (!lastInputEdit) return null;
  const source = asString(lastInputEdit.source) || "unknown";
  const actor =
    asString(lastInputEdit.actorSubjectId) ||
    asString(lastInputEdit.actorPermissionProfileName) ||
    asString(lastInputEdit.jobId) ||
    null;
  const inputKeys = Array.isArray(lastInputEdit.changedInputKeys)
    ? lastInputEdit.changedInputKeys
        .filter(
          (key): key is string =>
            typeof key === "string" && key.trim().length > 0,
        )
        .slice(0, 6)
    : [];
  const relationshipKeys = Array.isArray(lastInputEdit.changedRelationshipKeys)
    ? lastInputEdit.changedRelationshipKeys
        .filter(
          (key): key is string =>
            typeof key === "string" && key.trim().length > 0,
        )
        .slice(0, 6)
    : [];
  const changed = [
    inputKeys.length ? `inputs=${inputKeys.join(",")}` : null,
    relationshipKeys.length
      ? `relationships=${relationshipKeys.join(",")}`
      : null,
  ].filter(Boolean);
  return `lastEdit=${source}${actor ? ` by ${actor}` : ""}${
    changed.length ? ` (${changed.join("; ")})` : ""
  }`;
}

function artifactIssueSummary(record: UnknownRecord): string | null {
  const validation = asRecord(record.validation);
  if (!validation) return null;
  const issues = Array.isArray(validation.issues)
    ? validation.issues
        .map((issue) => asRecord(issue))
        .filter((issue): issue is UnknownRecord => Boolean(issue))
        .slice(0, 3)
    : [];
  if (issues.length > 0) {
    return `issues=${issues
      .map((issue) => {
        const code = asString(issue.code) || asString(issue.kind) || "issue";
        const path = asString(issue.path);
        const message = trimString(issue.message);
        return `${code}${path ? ` at ${path}` : ""}${
          message ? ` (${message})` : ""
        }`;
      })
      .join("; ")}`;
  }
  const error =
    trimString(validation.error) ||
    trimString(validation.reason) ||
    trimString(validation.message);
  return error ? `validation=${error}` : null;
}

function artifactExecutionSummary(metadata: UnknownRecord): string | null {
  const execution = asRecord(metadata.execution);
  if (!execution) return null;
  const result = asRecord(execution.result);
  const awaiting = asString(result?.awaiting) || asString(execution.awaiting);
  const pendingTransition =
    asString(result?.pendingTransition) ||
    asString(execution.pendingTransition);
  const approval = asRecord(result?.approval) || asRecord(execution.approval);
  const approvalTarget =
    asString(approval?.permissionProfileName) ||
    asString(approval?.permissionProfileId) ||
    asString(approval?.assigneeSubjectId);
  const error = trimString(execution.error);
  const pieces = [
    awaiting ? `awaiting=${awaiting}` : null,
    pendingTransition ? `pendingTransition=${pendingTransition}` : null,
    approvalTarget ? `approvalTarget=${approvalTarget}` : null,
    error ? `executionError=${error}` : null,
  ].filter(Boolean);
  return pieces.length ? pieces.join("; ") : null;
}

function artifactStatePathSummary(metadata: UnknownRecord): string | null {
  const statePlan = asRecord(metadata.statePlan);
  if (!statePlan) return null;
  const machineName = asString(statePlan.machineName);
  const targetState = asString(statePlan.targetState);
  const objectPath = asString(statePlan.objectPath);
  const approvedTransitions = Array.isArray(statePlan.approvedTransitions)
    ? statePlan.approvedTransitions.length
    : 0;
  const approvalDecisions = Array.isArray(statePlan.approvalDecisions)
    ? statePlan.approvalDecisions.length
    : 0;
  const pieces = [
    machineName || targetState
      ? `statePath=${machineName || "state_machine"}${
          targetState ? ` -> ${targetState}` : ""
        }`
      : null,
    objectPath ? `objectPath=${objectPath}` : null,
    approvedTransitions ? `approvedTransitions=${approvedTransitions}` : null,
    approvalDecisions ? `approvalDecisions=${approvalDecisions}` : null,
  ].filter(Boolean);
  return pieces.length ? pieces.join("; ") : null;
}

function artifactSummaryLine(
  artifactId: string,
  record: UnknownRecord | undefined,
): string {
  if (!record) return `- ${artifactId}: unavailable in session artifact store`;

  const label = trimString(record.label) || artifactId;
  const kind = asString(record.kind) || "artifact";
  const status = asString(record.status) || "unknown";
  const createdByJobId = asString(record.createdByJobId);
  const target = asRecord(record.target);
  const metadata = asRecord(record.metadata) || {};
  const subArtifactIds = Array.isArray(record.subArtifactIds)
    ? record.subArtifactIds
        .filter(
          (id): id is string => typeof id === "string" && id.trim().length > 0,
        )
        .slice(0, 8)
    : [];
  const relationships = compactJson(record.relationships, 220);
  const pieces = [
    `kind=${kind}`,
    `status=${status}`,
    createdByJobId ? `createdByJob=${createdByJobId}` : null,
    target
      ? `target=${asString(target.className) || "record"}:${
          asString(target.id) || "unknown"
        }${asString(target.label) ? ` (${asString(target.label)})` : ""}`
      : null,
    artifactStatePathSummary(metadata),
    artifactExecutionSummary(metadata),
    artifactIssueSummary(record),
    latestInputEditSummary(metadata),
    subArtifactIds.length ? `subArtifacts=${subArtifactIds.join(",")}` : null,
    relationships ? `relationships=${relationships}` : null,
  ].filter(Boolean);

  return `- ${artifactId}: ${label}${pieces.length ? `; ${pieces.join("; ")}` : ""}`;
}

function buildArtifactHistory(
  show?: SessionTranscriptShowRefs,
  artifactsById?: Record<string, UnknownRecord>,
): string | undefined {
  if (!show) return undefined;
  const artifactIds = show.sessionArtifactIds || [];
  const actionSuggestions = show.actionSuggestions || [];
  if (artifactIds.length === 0 && actionSuggestions.length === 0) {
    return `[Agent message]\n${stringifyTranscriptValue({ show }, "")}`;
  }
  const lines = artifactIds
    .slice(0, 8)
    .map((artifactId) =>
      artifactSummaryLine(artifactId, artifactsById?.[artifactId]),
    );
  if (artifactIds.length > 8) {
    lines.push(`- ${artifactIds.length - 8} more artifacts omitted`);
  }
  if (actionSuggestions.length > 0) {
    if (artifactIds.length > 0) lines.push("[Agent suggested actions]");
    for (const suggestion of actionSuggestions.slice(0, 8)) {
      lines.push(
        `- ${suggestion.label}${
          suggestion.description ? `; ${suggestion.description}` : ""
        }`,
      );
    }
    if (actionSuggestions.length > 8) {
      lines.push(`- ${actionSuggestions.length - 8} more suggestions omitted`);
    }
  }
  const otherRefs = {
    entryPaths: show.entryPaths,
    listNames: show.listNames,
    variableNames: show.variableNames,
    fileIds: show.fileIds,
  };
  const hasOtherRefs = Object.values(otherRefs).some(
    (value) => Array.isArray(value) && value.length > 0,
  );
  const title =
    artifactIds.length > 0
      ? "[Agent displayed session artifacts]"
      : "[Agent suggested actions]";
  return [
    title,
    ...lines,
    hasOtherRefs
      ? `Other shown refs: ${stringifyTranscriptValue(otherRefs, "")}`
      : null,
  ]
    .filter(Boolean)
    .join("\n");
}

function feedItemShow(item: FeedItem): SessionTranscriptShowRefs | undefined {
  switch (item.kind) {
    case "objects": {
      const entryPaths: string[] = [];
      const listNames: string[] = [];
      const variableNames: string[] = [];
      for (const ref of item.payload.refs) {
        if (ref.type === "entry") entryPaths.push(ref.path);
        if (ref.type === "list") listNames.push(ref.name);
        if (ref.type === "variable") variableNames.push(ref.name);
      }
      return {
        ...(entryPaths.length ? { entryPaths } : {}),
        ...(listNames.length ? { listNames } : {}),
        ...(variableNames.length ? { variableNames } : {}),
      };
    }
    case "table":
      return {
        tables: [
          {
            id: item.payload.tableId,
            label: item.payload.label,
            columns: item.payload.columns,
            rows: item.payload.rows,
          },
        ],
      };
    case "artifact":
      return { sessionArtifactIds: [item.payload.artifactId] };
    case "file":
      return { fileIds: [item.payload.fileId] };
    case "action_suggestion":
      return {
        actionSuggestions: [
          {
            suggestionId: item.payload.suggestionId,
            label: item.payload.label,
            description: item.payload.description,
            target: item.payload.target
              ? { ...item.payload.target }
              : undefined,
            artifact: item.payload.proposedArtifact
              ? { ...item.payload.proposedArtifact }
              : undefined,
          } satisfies SessionTranscriptActionSuggestion,
        ],
      };
    default:
      return undefined;
  }
}

/**
 * Deliberate model/history projection from canonical durable occurrences.
 * Sequence is the only ordering key; routine and transient feedback are not
 * included in the next-turn transcript.
 */
export function buildSessionTranscriptFromFeedItems(input: {
  items: FeedItem[];
  liveDoc?: UnknownRecord | null;
}): SessionTranscriptEntry[] {
  const artifactsById = artifactRecordsById(input.liveDoc);
  const entries: SessionTranscriptEntry[] = [];

  for (const item of [...input.items].sort(
    (left, right) => left.sequence - right.sequence,
  )) {
    if (item.kind === "feedback") continue;

    if (item.kind === "message") {
      if (item.payload.role === "system") continue;
      const content = item.payload.text;
      entries.push({
        id: item.id,
        role: item.payload.role,
        content,
        timestamp: item.occurredAt,
        sequence: item.sequence,
        jobId: item.source?.jobId,
        promptId: item.payload.inReplyToPromptId,
        historyContent:
          item.payload.role === "assistant"
            ? `[Assistant reply]\n${content}`
            : undefined,
        source: "feed",
      });
      continue;
    }

    if (item.kind === "prompt") {
      const content = item.payload.message || item.payload.title;
      entries.push({
        id: item.id,
        role: "assistant",
        content,
        timestamp: item.occurredAt,
        sequence: item.sequence,
        jobId: item.source?.jobId,
        promptId: item.payload.promptId,
        historyContent: `[Assistant reply]\n${content}`,
        source: "feed",
      });
      continue;
    }

    const show = feedItemShow(item);
    if (!show) continue;
    entries.push({
      id: item.id,
      role: "assistant",
      content: "",
      timestamp: item.occurredAt,
      sequence: item.sequence,
      jobId: item.source?.jobId,
      show,
      historyContent: buildArtifactHistory(show, artifactsById),
      source: "feed",
    });
  }

  return entries;
}

const CANONICAL_FEED_REQUIRED =
  "Canonical session feed-v1 is required; past message/job transcript reconstruction is not supported.";

/**
 * Build a transcript only from feed-v1. A missing activation marker fails
 * closed instead of falling back to independently ordered message or job data.
 */
export function buildSessionTranscript(input: {
  liveDoc?: UnknownRecord | null;
  canonicalFeedItems?: FeedItem[];
}): SessionTranscriptEntry[] {
  const liveDoc = input.liveDoc || null;
  if (!isCanonicalSessionFeedDocument(liveDoc)) {
    throw new Error(CANONICAL_FEED_REQUIRED);
  }
  const snapshot = readSessionFeedSnapshot(liveDoc);
  if (snapshot.error) throw snapshot.error;
  return buildSessionTranscriptFromFeedItems({
    items: input.canonicalFeedItems || snapshot.tail,
    liveDoc,
  });
}
